import { useEffect, useMemo } from 'react';

type ConnectionWithId = { id: string };

/**
 * Resolves which connection id should be selected for the current list.
 * Keeps the current value when still present; otherwise picks the first item
 * (callers should pass connections sorted by lastUsedForStationAt desc).
 * Returns the id synchronously so callers do not flash an empty state.
 * Also keeps controlled state in sync via {@link onChange}.
 */
export function useAutoSelectConnectionId(
  connections: ConnectionWithId[],
  value: string | null,
  onChange: (connectionId: string | null) => void,
): string | null {
  const resolvedId = useMemo(() => {
    if (connections.length === 0) {
      return null;
    }
    if (value && connections.some((connection) => connection.id === value)) {
      return value;
    }
    return connections[0].id;
  }, [connections, value]);

  useEffect(() => {
    if (resolvedId !== value) {
      onChange(resolvedId);
    }
  }, [resolvedId, value, onChange]);

  return resolvedId;
}
